import { Link, useLocation } from "react-router-dom";
import { ChevronLeft, ChevronRight, Home, Tv, Download, X, List, Film } from "lucide-react";
import { useSidebar } from "../contexts/SidebarContext";
import { useEffect, useState } from "react";

const Sidebar = () => {
  const { isCollapsed, toggleCollapse, isMobileOpen, closeMobile } = useSidebar();
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(false);

  // Check if mobile (sidebar hidden at 1000px and below)
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 1000);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Close mobile sidebar when route changes
  useEffect(() => {
    if (isMobile) {
      closeMobile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const isActive = (path) => {
    if (path === "/" && location.pathname === "/") return true;
    if (path === "/movies" && location.pathname.startsWith("/movies")) return true;
    if (path === "/anime" && location.pathname.startsWith("/anime")) return true;
    if (path !== "/" && path !== "/movies" && path !== "/anime" && location.pathname.startsWith(path)) return true;
    return false;
  };

  const navItems = [
    { path: "/", label: "Home", icon: Home },
    { path: "/movies", label: "Movies", icon: Film },
    { path: "/tvshows", label: "TV Shows", icon: Tv },
    { path: "/anime", label: "Anime", icon: Film },
    { path: "/genres", label: "Genres", icon: List },
    { path: "/download", label: "Download App", icon: Download },
  ];

  return (
    <>
      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/80 backdrop-blur-sm z-[9999] transition-opacity duration-300"
          onClick={closeMobile}
        />
      )}

      <aside
        className={`fixed left-0 top-0 h-full bg-black/95 backdrop-blur-md border-r border-white/10 z-[10000] transition-all duration-300 ease-in-out ${
          isCollapsed ? "lg:w-20 w-64" : "w-64"
        } ${
          isMobileOpen ? "translate-x-0" : "-translate-x-full tablet:translate-x-0"
        }`}
      >
        {/* Sidebar Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          {!isCollapsed && (
            <Link to="/" className="flex items-center group flex-1" onClick={closeMobile}>
              <h1 className="cursor-pointer text-red font-bold poppins text-xl tracking-tight transition-all duration-300 group-hover:scale-105">
                FilmJetHub
              </h1>
            </Link>
          )}
          {isCollapsed && (
            <Link to="/" className="flex items-center justify-center w-full" onClick={closeMobile}>
              <div className="w-10 h-10 bg-red rounded-lg flex items-center justify-center">
                <span className="text-white font-bold poppins text-lg">F</span>
              </div>
            </Link>
          )}
          
          {/* Close Button (Mobile only) */}
          <button
            onClick={closeMobile}
            className="lg:hidden flex items-center justify-center w-8 h-8 rounded-lg hover:bg-white/10 text-white transition-all duration-300 hover:text-red"
            aria-label="Close menu"
          >
            <X size={20} />
          </button>

          {/* Collapse Toggle Button (Desktop only) */}
          <button
            onClick={toggleCollapse}
            className="hidden lg:flex items-center justify-center w-8 h-8 rounded-lg hover:bg-white/10 text-white transition-all duration-300 hover:text-red"
            aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {isCollapsed ? (
              <ChevronRight size={20} />
            ) : (
              <ChevronLeft size={20} />
            )}
          </button>
        </div>

        {/* Navigation Links */}
        <nav className="flex flex-col p-4 space-y-2">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            
            if (item.external) {
              return (
                <a
                  key={item.path}
                  href={item.path}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-300 group relative ${
                    active
                      ? "bg-red/20 text-red border-l-4 border-red"
                      : "text-white/70 hover:bg-white/10 hover:text-white"
                  }`}
                  onClick={closeMobile}
                >
                  <Icon
                    size={20}
                    className={`flex-shrink-0 ${active ? "text-red" : "group-hover:text-red"}`}
                  />
                  {!isCollapsed && (
                    <span className="mont font-medium text-[15px]">{item.label}</span>
                  )}
                  {isCollapsed && (
                    <div className="absolute left-full ml-2 px-2 py-1 bg-black/95 backdrop-blur-md rounded text-white text-sm mont opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-300 whitespace-nowrap border border-white/10 z-50">
                      {item.label}
                    </div>
                  )}
                </a>
              );
            }

            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-300 group relative ${
                  active
                    ? "bg-red/20 text-red border-l-4 border-red"
                    : "text-white/70 hover:bg-white/10 hover:text-white"
                }`}
                onClick={closeMobile}
              >
                <Icon
                  size={20}
                  className={`flex-shrink-0 ${active ? "text-red" : "group-hover:text-red"}`}
                />
                {!isCollapsed && (
                  <span className="mont font-medium text-[15px]">{item.label}</span>
                )}
                {isCollapsed && (
                  <div className="absolute left-full ml-2 px-2 py-1 bg-black/95 backdrop-blur-md rounded text-white text-sm mont opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-300 whitespace-nowrap border border-white/10">
                    {item.label}
                  </div>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Sidebar Footer (when expanded) */}
        {!isCollapsed && (
          <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/10">
            <p className="text-white/40 mont text-xs text-center">
              © {new Date().getFullYear()} FilmJetHub
            </p>
          </div>
        )}
      </aside>
    </>
  );
};

export default Sidebar;
